import merge from 'lodash.merge';
import { LegacyRef, MutableRefObject, RefCallback } from 'react';
import { EXTRA_PALETTE_COLOURS, ExtraPaletteColour, STATUS_COLOURS, StatusColour } from '../fundamentals/colours/variants';
import { PaletteKey, StateColour } from '../fundamentals/colours/colourInterfaces';
import { BaseColours } from '../fundamentals/colours/baseColours';

const HASH_MULTIPLIER = 31;
/* eslint-disable unicorn/prefer-code-point*/
export const getStringHash = (str: string) => Math.abs([...str].reduce((s, c) => Math.trunc(Math.imul(HASH_MULTIPLIER, s) + c.charCodeAt(0)), 0));

export const chunkPalette = (palette: BaseColours['palette']) => {
  let entries: Partial<Record<StatusColour | ExtraPaletteColour, Partial<Record<PaletteKey, string>>[]>> = {};

  [...Object.values(STATUS_COLOURS), ...Object.values(EXTRA_PALETTE_COLOURS)].forEach((colour) => {
    const filtered = Object.entries(palette).filter(([key]) => key.startsWith(colour));
    const split = filtered.splice(filtered.length / 2);

    const top = Object.assign({}, ...filtered.map(([k, v]) => ({ [k]: v })));
    const bottom = Object.assign({}, ...split.map(([k, v]) => ({ [k]: v })));

    entries[colour as StatusColour | ExtraPaletteColour] = [top, bottom];
  });

  return entries;
};

export const splitDefaultFromLight = ({ light, text, colour, hover, active }: StateColour) => [
  {
    colour,
    hover,
    active,
    text
  },
  light
];

export const getHexOpacity = (opacity: number) =>
  Math.round((opacity / 100) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();

export const resolveProps = <T, A>(contextProps: A, props: T): T & A => merge(contextProps, props);

export const formatBytes = (bytes: number, decimals: number = 0) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1000;
  const dm = Math.max(0, decimals);
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const printArray = (items?: string[], conjunction?: string) => {
  if (!items || items?.length === 0) return;
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} ${conjunction ?? '&'} ${items.at(-1)}`;
};

// From https://github.com/gregberge/react-merge-refs
export const mergeRefs =
  <T = any>(refs: Array<MutableRefObject<T> | LegacyRef<T>>): RefCallback<T> =>
  (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
        // eslint-disable-next-line unicorn/no-null
      } else if (ref != null) {
        (ref as MutableRefObject<T | null>).current = value;
      }
    });
  };

export const generateRandomId = (prefix?: string, postfix?: string | number, length = 6) =>
  `${prefix ? `${prefix}-` : ``}${Math.random()
    .toString(36)
    .slice(2, length + 2)}${postfix ? `-${postfix}` : ``}`;
